import { Injectable } from '@angular/core';
import { SERVER_API_URL } from '../app.constants';
import { LabelPrintingContext } from './label-printing.context';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { LabelType, OriginResourceMetadata, PresetSetting, Sortable } from './label-printing.model';
import { ParamContext } from '../shared/service/param.context';
import { LabelPrintingType } from './label-printing.component';

@Injectable()
export class LabelPrintingService {

    private baseUrl = SERVER_API_URL;

    constructor(
        private http: HttpClient,
        private context: LabelPrintingContext,
        private paramContext: ParamContext
    ) {
    }

    getLabelsNeededSummary() {
        const printingLabelType = this.context.printingLabelType;
        const resourceUrl = `crops/${this.paramContext.cropName}/programs/${this.paramContext.programUUID}/labelPrinting/${printingLabelType}/labels/summary`;
        return this.http.post(this.baseUrl + resourceUrl, this.createLabelPrintingRequestBody());

    }

    getOriginResourceMetadada(): Observable<OriginResourceMetadata> {
        const printingLabelType = this.context.printingLabelType;
        const resourceUrl = `crops/${this.paramContext.cropName}/programs/${this.paramContext.programUUID}/labelPrinting/${printingLabelType}/metadata`;
        return this.http.post<OriginResourceMetadata>(this.baseUrl + resourceUrl, this.createLabelPrintingRequestBody());
    }

    getAvailableLabelFields(): Observable<LabelType[]> {
        const printingLabelType = this.context.printingLabelType;
        const resourceUrl = `crops/${this.paramContext.cropName}/programs/${this.paramContext.programUUID}/labelPrinting/${printingLabelType}/labelTypes`;
        return this.http.post<LabelType[]>(this.baseUrl + resourceUrl, this.createLabelPrintingRequestBody());
    }

    getSortableFields(): Observable<Sortable[]> {
        const printingLabelType = this.context.printingLabelType;
        const resourceUrl = `crops/${this.paramContext.cropName}/programs/${this.paramContext.programUUID}/labelPrinting/${printingLabelType}/sortable-fields`;
        return this.http.get<Sortable[]>(this.baseUrl + resourceUrl);
    }

    download(fileExtension: any, labelsGeneratorInput: any) {
        const printingLabelType = this.context.printingLabelType;
        const resourceUrl = `crops/${this.paramContext.cropName}/programs/${this.paramContext.programUUID}/labelPrinting/${printingLabelType}/labels/${fileExtension}`;
        return this.http.post(`${this.baseUrl + resourceUrl}`, labelsGeneratorInput,
            {
                responseType: 'blob',
                observe: 'response'
            }
        );
    }

    getAllPresets(toolSection): Observable<PresetSetting[]> {
        const options: HttpParams = new HttpParams()
            .append('toolId', '23')
            .append('toolSection', toolSection);

        const resourceUrl = `crops/${this.paramContext.cropName}/programs/${this.paramContext.programUUID}/presets`;
        return this.http.get<PresetSetting[]>(this.baseUrl + resourceUrl, {
            params: options,
        });
    }

    addPreset(preset: PresetSetting): Observable<PresetSetting> {
        const resourceUrl = `crops/${this.paramContext.cropName}/programs/${this.paramContext.programUUID}/presets`;
        return this.http.put<PresetSetting>(this.baseUrl + resourceUrl, preset);
    }

    updatePreset(preset: PresetSetting): Observable<void> {
        const resourceUrl = `crops/${this.paramContext.cropName}/programs/${this.paramContext.programUUID}/presets/${preset.id}`;
        return this.http.put<void>(this.baseUrl + resourceUrl, preset);
    }

    deletePreset(presetId: number): Observable<PresetSetting> {
        const options: HttpParams = new HttpParams()
            .append('presetId', presetId.toString());
        const resourceUrl = `crops/${this.paramContext.cropName}/programs/${this.paramContext.programUUID}/presets/${presetId}`;
        return this.http.delete<PresetSetting>(this.baseUrl + resourceUrl);
    }

    getDefaultSettings() {
        const resourceUrl = `crops/${this.paramContext.cropName}/programs/${this.paramContext.programUUID}/labelPrinting/${this.context.printingLabelType}/default-settings`;
        return this.http.post<PresetSetting>(this.baseUrl + resourceUrl, this.createLabelPrintingRequestBody());
    }

    validateLabelsInfoInputData(request: any, labelPrintingType: LabelPrintingType): Observable<void> {
        const resourceUrl = `crops/${this.paramContext.cropName}/programs/${this.paramContext.programUUID}/labelPrinting/${labelPrintingType}/labels/validate`;
        return this.http.post<void>(this.baseUrl + resourceUrl, request);
    }


    private createLabelPrintingRequestBody(): any {
        const requestBody = {
            datasetId: this.context.datasetId,
            studyId: this.context.studyId,
            selectedGIDs: null,
            isBrowseAllGermplasmResults: false,
            selectedLotUUIDs: null,
            searchRequestId: this.context.searchRequestId,
            sampleListId: this.context.sampleListId,
            selectedColumns: this.context.selectedColumns,
            listId: this.context.listId
        };
        if (this.context.printingLabelType === LabelPrintingType.GERMPLASM) {
            requestBody.selectedGIDs = this.context.selectedGIDs;
            requestBody.isBrowseAllGermplasmResults = this.context.isBrowseAllGermplasmResults;
        }
        if (this.context.printingLabelType === LabelPrintingType.LOT) {
            requestBody.selectedLotUUIDs = this.context.selectedLotUUIDs;
        }

        return requestBody;
    }
}
